// ============================================================================
// ASAI Course Recommendation — Rule-Based Engine
// Deterministic scoring across the central PROGRAMS_DATA. No external API.
// Returns programs ranked by fit score with a short "why this fits you" list.
// ============================================================================
import { PROGRAMS_DATA } from "./programsData";

// ---- Option lists (used by the form) ----
export const TECH_LEVELS = ["None", "Basic", "Intermediate", "Advanced"];
export const CAREER_GOALS = [
  "AI Engineer / ML Engineer",
  "Data Scientist / Data Analyst",
  "Generative AI Developer",
  "Cloud / DevOps Engineer",
  "Software Developer",
  "Digital Payments / Fintech Role",
  "Explore AI (unsure yet)",
];
export const DURATIONS = ["1 Month", "3 Months", "6 Months", "1 Year", "Flexible"];
export const MODES = ["Online", "Offline", "Hybrid", "No Preference"];
export const BUDGETS = ["Free only", "Under ₹20,000", "₹20,000 – ₹40,000", "₹40,000+", "Flexible"];
export const INTEREST_TAGS = [
  "Artificial Intelligence",
  "Generative AI",
  "Machine Learning",
  "Python",
  "Cloud Computing",
  "DevOps",
  "SQL",
  "Linux",
  "UPI and Digital Payments",
  "Software Development",
  "Career Preparation",
];

// ---- Duration mapping to months (for numeric comparison) ----
const DUR_MONTHS = {
  "1 Month": 1,
  "3 Months": 3,
  "6 Months": 6,
  "1 Year": 12,
};
const programMonths = (p) => DUR_MONTHS[p.duration] || 0;

// ---- Fee (numeric approx.) ----
const feeAmount = (p) => {
  if (p.isFree) return 0;
  const m = (p.fee || "").replace(/[^\d]/g, "");
  return m ? parseInt(m, 10) : 0;
};

// ---- Curriculum keyword bank per program (derived) ----
const curriculumTokens = (p) =>
  (p.curriculum || []).map((s) => s.toLowerCase());

// ---- Career goal → topic weight ----
const GOAL_MATCHES = {
  "AI Engineer / ML Engineer": ["artificial intelligence", "machine learning", "python", "generative ai"],
  "Data Scientist / Data Analyst": ["data science", "sql", "python", "machine learning"],
  "Generative AI Developer": ["generative ai", "prompt", "python", "ai tools"],
  "Cloud / DevOps Engineer": ["cloud", "aws", "azure", "devops", "linux"],
  "Software Developer": ["full-stack", "python", "sql", "linux"],
  "Digital Payments / Fintech Role": ["upi", "npci", "payments", "settlement", "production support"],
  "Explore AI (unsure yet)": ["artificial intelligence", "machine learning", "generative ai"],
};

// ---- Interest tags → topics ----
const INTEREST_MATCHES = {
  "Artificial Intelligence": ["artificial intelligence"],
  "Generative AI": ["generative ai", "prompt"],
  "Machine Learning": ["machine learning"],
  "Python": ["python"],
  "Cloud Computing": ["cloud", "aws", "azure"],
  "DevOps": ["devops"],
  "SQL": ["sql"],
  "Linux": ["linux"],
  "UPI and Digital Payments": ["upi", "npci", "payments", "settlement"],
  "Software Development": ["full-stack", "python", "linux", "sql"],
  "Career Preparation": ["placement", "interview", "resume", "soft skills", "career support"],
};

// ---- Budget bracket check ----
const budgetFits = (program, budget) => {
  const amt = feeAmount(program);
  switch (budget) {
    case "Free only":
      return program.isFree ? { ok: true, delta: 0 } : { ok: false, delta: amt };
    case "Under ₹20,000":
      return amt <= 20000 ? { ok: true, delta: 0 } : { ok: false, delta: amt - 20000 };
    case "₹20,000 – ₹40,000":
      return amt >= 20000 && amt <= 40000
        ? { ok: true, delta: 0 }
        : { ok: false, delta: Math.abs(amt - 30000) };
    case "₹40,000+":
      return amt >= 40000 ? { ok: true, delta: 0 } : { ok: false, delta: 40000 - amt };
    case "Flexible":
    default:
      return { ok: true, delta: 0 };
  }
};

// ---- Score a single program vs. the user profile ----
function scoreProgram(p, profile) {
  const reasons = [];
  let score = 0;

  // 1. Career goal match (max 30)
  const goalKws = GOAL_MATCHES[profile.careerGoal] || [];
  const curric = curriculumTokens(p);
  const goalHits = goalKws.filter((kw) => curric.some((c) => c.includes(kw))).length;
  if (goalKws.length > 0) {
    const goalScore = Math.round((goalHits / goalKws.length) * 30);
    score += goalScore;
    if (goalHits >= Math.ceil(goalKws.length / 2)) {
      reasons.push(`Strong match for your goal — ${profile.careerGoal}`);
    }
  }

  // 2. Interests match (max 25)
  const interestArr = (profile.interests || []).flatMap((tag) => INTEREST_MATCHES[tag] || []);
  if (interestArr.length > 0) {
    const uniqueHits = new Set(interestArr.filter((kw) => curric.some((c) => c.includes(kw))));
    const interestScore = Math.min(25, uniqueHits.size * 5);
    score += interestScore;
    if (uniqueHits.size >= 2) {
      reasons.push(`Covers ${uniqueHits.size} of your chosen interests`);
    }
  }

  // 3. Duration match (max 20)
  const pref = profile.preferredDuration;
  if (pref && pref !== "Flexible" && DUR_MONTHS[pref] != null) {
    const diff = Math.abs(programMonths(p) - DUR_MONTHS[pref]);
    if (diff === 0) {
      score += 20;
      reasons.push(`Matches your preferred duration (${p.duration})`);
    } else if (diff <= 3) score += 12;
    else if (diff <= 6) score += 5;
  } else {
    score += 10; // flexible = mild boost
  }

  // 4. Budget fit (max 20)
  const b = budgetFits(p, profile.budget);
  if (b.ok) {
    score += 20;
    if (profile.budget && profile.budget !== "Flexible") {
      reasons.push(`Fits within your budget (${p.fee})`);
    }
  } else {
    // Partial credit: still show but penalize
    score -= 10;
  }

  // 5. Technical background boost (max 15)
  const level = profile.technicalBackground;
  if (level === "None" || level === "Basic") {
    if (p.slug === "free-ai-ml" || p.slug === "3-month-internship") {
      score += 15;
      reasons.push("Beginner-friendly — starts from the fundamentals");
    } else if (p.slug === "free-upi") {
      score += 10;
    }
  } else if (level === "Intermediate") {
    if (p.slug === "6-month-industrial-training" || p.slug === "3-month-internship") {
      score += 15;
      reasons.push("A strong step-up for intermediate learners");
    }
  } else if (level === "Advanced") {
    if (p.slug === "1-year-job-oriented" || p.slug === "6-month-industrial-training") {
      score += 15;
      reasons.push("Deep, industry-grade track suited for advanced learners");
    }
  }

  // 6. Mode preference (max 5)
  if (profile.preferredMode && profile.preferredMode !== "No Preference") {
    // Every program supports Online/Offline/Hybrid — small guaranteed boost
    score += 5;
  }

  // 7. Highlighted flagship program tiebreaker
  if (p.highlight) score += 2;

  // Fallback reasons so we always show something
  if (reasons.length === 0) {
    reasons.push(`Covers ${p.duration.toLowerCase()} of focused, hands-on learning`);
  }

  return { program: p, score: Math.max(0, score), reasons: reasons.slice(0, 3) };
}

/**
 * Recommend programs for a user profile.
 * Returns the top `limit` results sorted by score (desc), each with reasons.
 * `profile` = {
 *   careerGoal, interests (array), preferredDuration, preferredMode,
 *   budget, technicalBackground
 * }
 */
export function recommendPrograms(profile, limit = 3) {
  const scored = PROGRAMS_DATA.map((p) => scoreProgram(p, profile));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}
